import {JSXElement, Show, createSignal, createEffect, on, Setter, onMount} from 'solid-js'
import {useSelector, useDispatch} from '../../store'
import {useIcon} from '../icon/useIcon'
import {unsetReminder, snoozeReminder} from '@te/volt-store/lib/actions'
import {FocusGroupProvider, FocusableProvider} from '../focus/index'
import {useI18n} from '../../i18n'
import {isPlatform, isWeb} from '../../utils'
import webStyle from './ReminderWeb.module.css'
import stvStyle from './RemindeStv.module.css'
import {useSpatialNavigation} from '../../spatialNavigation'
import {useReminder} from './useReminder'

export enum ReminderPosition {
    TOP_LEFT = 'top-left',
    TOP_RIGHT = 'top-right',
    BOTTOM_LEFT = 'bottom-left',
    BOTTOM_RIGHT = 'bottom-right',
    TOP_CENTER = 'top-center',
}

export interface ReminderProps {
    title: string
    description: string
    subDescription?: string
    actions?: JSXElement[]
    index?: number
    onClose?: (id: any, props: any) => void
    className?: string
    id?: number
    life?: number
    position?: ReminderPosition
    sticky?: boolean
    link?: string
    programId?: string
    channelId?: string
    eventId?: string
    pendingTime?: string
    handlePlay?: any
    onCancelReminder?: any
    autoClosed?: boolean
    onReminderHandleFocus?: any
    deleteReminder?: any
    updateLocalShowedReminder?: any
    iconType?: any
    setAutoClosed?: Setter<boolean>
    packages?: any
}

const ActionBtn = (props: any) => {
    const style = isWeb ? webStyle : stvStyle
    return (
        <FocusableProvider
            focusable={props?.focusable ?? true}
            id={props?.primaryKey}
            focusKey={props?.primaryKey}
            onEnterPress={props?.action}
            class={style.reminderActionbutton}
            onFocus={() => {
                var element = document.querySelector(style.reminderProgramDetails)
                if (element) {
                    element.scrollTo({top: 0, behavior: 'smooth'})
                }
            }}
            onArrowPress={(direction: any) => {
                if (direction == 'up' || direction == 'down') {
                    return false
                }
            }}
        >
            {(focused: any) => (
                <div
                    class={`${style.actionbtn} ${!props.focusable && style.disabled}  ${focused?.() ? style.reminderActionBtnFocused : ''}`}
                    onClick={props.focusable && props?.action}
                >
                    {props?.icon}
                    {props?.value ? props?.value : ''}
                </div>
            )}
        </FocusableProvider>
    )
}

export const Reminder = (props: ReminderProps) => {
    const style = isWeb ? webStyle : stvStyle
    const dispatch = useDispatch()
    const icons = useIcon()
    const {getI18n} = useI18n()
    const landscapeImg = icons.get('landscape')
    const {shownReminders} = useReminder()

    const {setFocus} = useSpatialNavigation()

    const programsState = useSelector((s: any) => s.programs)
    const channelsState = useSelector((s: any) => s.channels)
    // const randomStringForFocusKey = generateRandomString(5)
    const [program, setProgram] = createSignal<any>(undefined)
    const [channel, setChannel] = createSignal<any>(undefined)
    const [disableWatch, setDisableWatch] = createSignal<boolean>(false)

    const handleIndexValue: any = () => {
        return `${(props?.index || 0) * 80 + 15}px`
    }

    const PlayIcon = icons.get('Play')
    const BellSnoozeIcon = icons.get('bellSnooze')
    const BellRingsOffIcon = icons.get('bellRingsOff')

    const getStyle = (): string => {
        switch (props?.position) {
            case ReminderPosition.TOP_CENTER:
            case ReminderPosition.TOP_RIGHT:
            case ReminderPosition.TOP_LEFT: {
                return 'top'
            }
            case ReminderPosition.BOTTOM_RIGHT:
            case ReminderPosition.BOTTOM_LEFT: {
                return 'bottom'
            }
            default:
                return 'bottom'
        }
    }

    onMount(() => {
        if (!isWeb) {
            if (shownReminders()?.length > 0) {
                setFocus('reminder_watch_episode' + props?.eventId)
            }
        }
    })

    createEffect(
        on(
            () => props.eventId,
            () => {
                if (props.eventId) {
                    setProgram(programsState()?.[props.eventId])
                    setChannel(channelsState?.()?.data?.[program()?.channelId])
                }
            }
        )
    )

    createEffect(
        on(
            ()=> channel(),
            () =>{
                const isKidsProfile = localStorage.getItem("isKidsProfile")
                if(isKidsProfile === 'true' && !channel()?.isKidsChannel){
                    setDisableWatch(true)
                    snoozeReminderAvailbility(program()?.startTime) ? setFocus("snooze_reminder" + props?.eventId) : setFocus("stop_reminder" + props?.eventId)
                }
            }
        )
        
    )

    const onSnoozeReminder = () => {
        props?.onClose?.(props?.id, props)
        dispatch(snoozeReminder(props?.eventId))
        // setFocus("tile_details_update_favourite_VOD")

        setTimeout(() => {
            props?.deleteReminder?.(props?.id)
        }, 1000)
    }

    createEffect(() => {
        if (props?.autoClosed && props?.setAutoClosed) {
            if (snoozeReminderAvailbility(program()?.startTime)) {
                onSnoozeReminder()
            } else {
                // props?.deleteReminder(props?.eventId)
                // props?.onReminderHandleFocus?.(props?.eventId)
            }
            props?.setAutoClosed(false)
        }
    })

    const onCancelReminder = () => {
        props?.onCancelReminder(props?.eventId, props?.id)
        props?.onClose?.(props?.id, props)
    }

    const snoozeReminderAvailbility = (startTime: any) => {
        return new Date().getTime() < new Date(startTime).getTime() ? true : false
    }

    const calculatePendingTime = () => {
        let dateDiff = (program()?.startTime - new Date().getTime()) / 1000
        return Math.round(dateDiff / 60)
    }

    const getPosition = () => {
        switch (props.position) {
            case ReminderPosition.TOP_CENTER:
                return style.topCenter
            case ReminderPosition.BOTTOM_LEFT:
                return style.bottomLeft
            case ReminderPosition.TOP_LEFT:
                return style.topLeft
            case ReminderPosition.BOTTOM_RIGHT:
                return style.bottomRight
            case ReminderPosition.TOP_RIGHT:
                return style.topRight
            default:
                return style.bottomLeft
        }
    }

    return (
        <div>
            <FocusGroupProvider id={`reminder-screen`} focusKey={`reminder-screen`} isFocusBoundary={true}>
               
                <div
                    class={`${style.reminderIconMainWrapper} ${props?.className} ${getPosition()}`}
                    style={{[getStyle()]: handleIndexValue()}}
                >
                    <div
                        class={style.reminderIconDetailsReminderWrapper}
                        style={{ height: disableWatch() && !isWeb ? '150px' : '135px' }}
                    >
                        <div class="reminder-program-icon">
                            <img
                                class={style.reminderImage}
                                style={{ height: disableWatch() && !isWeb ? '150px' : '135px' }} src={program()?.thumbnail ?? program()?.logo ?? landscapeImg}
                            />
                        </div>
                        <div class={style.reminderProgramDetails}>
                            <div class={style.reminderTitle}>{program()?.serieTitle ?? program()?.title}</div>
                            <div class={style.reminderDescription}>
                                <div>
                                    {calculatePendingTime() < 0
                                        ? `This program has started ${calculatePendingTime() * -1} minutes ago`
                                        : calculatePendingTime() === 0
                                            ? `The program has started`
                                            : `The program starts in ${calculatePendingTime()} minutes`}
                                </div>
                                <div>
                                    {disableWatch() &&
                                        getI18n()?.formatMessage({ id: 'REMINDER_DESC' })
                                    }
                                </div>
                            </div>
                            <div class={style.actions}>
                                <ActionBtn
                                    value={getI18n()?.formatMessage({id: 'PLAY'})}
                                    primaryKey={'reminder_watch_episode' + props?.eventId}
                                    action={() => {
                                        dispatch(unsetReminder({eventId: props?.eventId}))
                                        props?.onClose?.(props?.id, props)
                                        props?.handlePlay(program(), channelsState()?.data?.[program().channelId])
                                        if (!isPlatform('laptop'))
                                            setTimeout(() => {
                                                props?.deleteReminder?.(props?.id)
                                            }, 2000)
                                    }}
                                    focusable={!disableWatch()}
                                    icon={<PlayIcon />}
                                />
                                <Show when={snoozeReminderAvailbility(program()?.startTime)}>
                                    <ActionBtn
                                        value={getI18n()?.formatMessage({id: 'SNOOZE'})}
                                        primaryKey={'snooze_reminder' + props?.eventId}
                                        action={() => {
                                            onSnoozeReminder()
                                        }}
                                        focusable={true}
                                        icon={<BellSnoozeIcon />}
                                    />
                                </Show>
                                <ActionBtn
                                    value={getI18n()?.formatMessage({id: 'STOP'})}
                                    primaryKey={'stop_reminder' + props?.eventId}
                                    action={() => {
                                        onCancelReminder()
                                    }}
                                    focusable={true}
                                    icon={<BellRingsOffIcon />}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </FocusGroupProvider>
        </div>
    )
}
